import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLandmark } from '@fortawesome/free-solid-svg-icons';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';
import { getJourney } from '../../../services/miceTrailService';

const heroImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-28.webp';
const grottoImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-91.webp';
const BOOKING_HREF = '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';

// Reveal a town by setting its name. Leave name blank until that week is announced.
const JOURNEY_STOPS = [
  { label: 'Week 1', name: '' },
  { label: 'Week 2', name: '' },
  { label: 'Week 3', name: '' },
  { label: 'Week 4', name: '' },
  { label: 'Week 5', name: '' },
  { label: 'The trail', name: 'Belvoir Castle', atCastle: true },
];

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  min-height: clamp(440px, 56vh, 620px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) {
    min-height: 0;
    align-items: stretch;
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 36% center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg,
      rgba(10,20,40,0.92) 0%,
      rgba(10,20,40,0.78) 32%,
      rgba(10,20,40,0.38) 58%,
      rgba(10,20,40,0.16) 100%
    ),
    linear-gradient(to top,
      rgba(10,20,40,0.55) 0%,
      rgba(10,20,40,0.12) 55%,
      rgba(10,20,40,0.2) 100%
    );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  min-width: 0;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.75rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.8rem, 6.5vw, 5.2rem);
  font-weight: 700;
  line-height: 0.98;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.5rem;
  max-width: 38rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(1rem, 1.7vw, 1.15rem);
  font-weight: 400;
  line-height: 1.65;
  color: rgba(255,255,255,0.88);
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: rgba(255,255,255,0.1);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.22);
`;

const HeroBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  @media (max-width: 640px) {
    flex-direction: column;
    a { width: 100%; text-align: center; box-sizing: border-box; }
  }
`;

const BtnPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 1rem 1.85rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const BtnGhost = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 1rem 1.75rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.55);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const Section = styled.section`
  padding: clamp(3.25rem, 6.5vw, 5.25rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: 1px solid ${C.border};
  scroll-margin-top: 180px;
`;

const Inner = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.55rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.85rem, 4vw, 2.75rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 40rem;
  margin: 0 0 clamp(1.75rem, 3vw, 2.5rem);
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.7;
  color: ${p => p.$light ? 'rgba(255,255,255,0.78)' : C.muted};
`;

const SignupWrap = styled.div`
  margin-top: 0.25rem;
`;

const SignupAside = styled.p`
  margin: 1.15rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.92rem;
  line-height: 1.55;
  color: ${C.muted};

  a {
    color: ${C.navy};
    font-weight: 600;
  }
`;

const ChapterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1rem, 2vw, 1.5rem);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Chapter = styled.article`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: clamp(1.5rem, 3vw, 2.15rem);
  border-radius: 16px;
  background: ${p => (p.$dark ? C.navy : '#fff')};
  color: ${p => (p.$dark ? '#fff' : C.navy)};
  border: 1px solid ${p => (p.$dark ? 'transparent' : C.border)};
  box-shadow: 0 8px 28px rgba(16, 29, 64, 0.06);
`;

const ChapterIndex = styled.p`
  margin: 0 0 0.85rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const ChapterTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.45rem, 2.4vw, 1.85rem);
  font-weight: 700;
  line-height: 1.15;
  color: inherit;
`;

const ChapterBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.98rem;
  line-height: 1.7;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.78)' : C.muted)};
`;

const FactRow = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin: auto 0 0;
  padding: 1.35rem 0 0;
  list-style: none;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Fact = styled.li`
  padding: 0.75rem 0.7rem;
  border-radius: 10px;
  background: ${p => (p.$onDark ? 'rgba(255,255,255,0.06)' : C.ivory)};
  text-align: center;

  strong {
    display: block;
    font-family: ${p => p.theme.fonts.main};
    font-size: 1.15rem;
    color: ${p => (p.$onDark ? '#fff' : C.navy)};
  }

  span {
    display: block;
    margin-top: 0.15rem;
    font-family: 'Inter', -apple-system, sans-serif;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    color: ${p => (p.$onDark ? 'rgba(255,255,255,0.62)' : 'rgba(16,29,64,0.55)')};
  }
`;

const ChapterLink = styled.a`
  margin-top: 0;
  padding-top: 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${p => (p.$light ? C.gold : C.navy)};
  text-decoration: none;
`;

const Timeline = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin: 0;
  padding: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 7px;
    left: calc(100% / 12);
    right: calc(100% / 12);
    height: 1px;
    background: rgba(16, 29, 64, 0.16);
  }

  @media (max-width: 760px) {
    display: flex;
    overflow-x: auto;
    padding-bottom: 0.35rem;
    &::before { display: none; }
  }
`;

const TimelineStop = styled.li`
  position: relative;
  text-align: center;
  min-width: 0;
  padding: 0 0.3rem;

  @media (max-width: 760px) {
    flex: 0 0 8.5rem;
  }
`;

const StopDot = styled.span`
  position: relative;
  z-index: 1;
  display: block;
  width: 15px;
  height: 15px;
  margin: 0 auto;
  border-radius: 50%;
  background: ${p => (p.$revealed ? C.gold : '#fff')};
  border: 2px solid ${p => (p.$revealed ? C.gold : 'rgba(16, 29, 64, 0.28)')};
`;

const StopLabel = styled.p`
  margin: 0.7rem 0 0.15rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
`;

const StopName = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(0.95rem, 1.4vw, 1.1rem);
  font-weight: 700;
  line-height: 1.25;
  color: ${p => (p.$hidden ? 'rgba(16, 29, 64, 0.38)' : C.navy)};
  font-style: ${p => (p.$hidden ? 'italic' : 'normal')};

  svg { width: 0.75rem; height: 0.75rem; color: ${C.gold}; }
`;

const StepGrid = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin: 0;
  padding: 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.li`
  padding: 1.35rem 1.2rem 1.45rem;
  background: #fff;
  border: 1px solid ${C.border};
  border-radius: 14px;
`;

const StepNum = styled.span`
  display: block;
  margin-bottom: 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: ${C.gold};
`;

const StepTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.2rem;
  font-weight: 700;
  color: ${C.navy};
`;

const StepBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${C.muted};
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
  gap: clamp(1.5rem, 4vw, 3rem);
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const ImgStrip = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);
  img {
    position: absolute;
    width: 168%;
    height: auto;
    max-width: none;
    left: -46%;
    top: 54%;
    transform: translateY(-62%);
    object-fit: fill;
    display: block;
  }
`;

const DetailList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const DetailItem = styled.li`
  padding: 0.95rem 0;
  border-top: 1px solid ${C.border};
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.55;
  color: ${C.muted};

  strong {
    display: block;
    margin-bottom: 0.15rem;
    font-family: ${p => p.theme.fonts.main};
    font-size: 1.05rem;
    font-weight: 700;
    color: ${C.navy};
  }

  &:last-child { border-bottom: 1px solid ${C.border}; }
`;

const VisitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: clamp(1.75rem, 3vw, 2.5rem);
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const VisitStep = styled.div`
  padding: clamp(1.2rem, 2.4vw, 1.6rem);
  background: ${p => p.$bg};
  color: #fff;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin-top: clamp(1.75rem, 3vw, 2.5rem);
  background: ${C.border};
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const InfoItem = styled.div`
  background: #fff;
  padding: 1.15rem 1.2rem;
`;

const InfoLabel = styled.div`
  margin-bottom: 0.3rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.4);
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${C.navy};
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 4.5vw, 3.1rem);
  font-weight: 700;
  line-height: 1.08;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 1.75rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.65;
  color: rgba(255,255,255,0.7);
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;

  @media (max-width: 640px) {
    flex-direction: column;
    a { width: 100%; box-sizing: border-box; text-align: center; }
  }
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 1rem 1.6rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.45);
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.15rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  color: rgba(255,255,255,0.4);
`;

const MiceTrail = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const [journey, setJourney] = useState(null);
  useEffect(() => { getJourney().then(setJourney).catch(() => {}); }, []);
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const stops = JOURNEY_STOPS.map((stop, index) => {
    const revealed = (journey?.revealed || []).find((item) => item.index === index + 1);
    const name = stop.atCastle ? stop.name : (revealed?.name || stop.name);
    const counts = journey?.currentTown && journey.currentTown === name ? journey.counts : null;
    return { ...stop, name, counts };
  });
  const nextHidden = stops.findIndex((stop) => !stop.atCastle && !stop.name);

  return (
    <>
      <Page className={pageClassName}>
        <Helmet>
          <title>Mice Trail | Christmas 2026 at Belvoir Castle</title>
          <meta name="description" content="Follow the Belvoir Mice home for Christmas 2026. Sign up for weekly town clues, then meet them on the Mice Trail inside the Castle — included with every Deck the Halls ticket." />
          <link rel="canonical" href="https://www.belvoircastle.com/christmas/mice-trail" />
          <meta property="og:title" content="The Mice Trail — Christmas 2026 at Belvoir Castle" />
          <meta property="og:description" content="Sign up to follow the mice through five local towns, then meet them on the Castle trail. Included with every Deck the Halls ticket." />
          <meta property="og:image" content={heroImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.belvoircastle.com/christmas/mice-trail" />
          <meta property="og:site_name" content="Belvoir Castle" />
          <meta property="og:locale" content="en_GB" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="The Mice Trail — Christmas 2026 at Belvoir Castle" />
          <meta name="twitter:description" content="Sign up to follow the mice through five local towns, then meet them on the Castle trail." />
          <meta name="twitter:image" content={heroImage} />
          <meta name="twitter:site" content="@belvoircastle" />
        </Helmet>

        <HeroWrap>
          <HeroImg src={heroImage} alt="Christmas at Belvoir Castle" />
          <HeroOverlay />
          <HeroContent>
            <HeroKicker>Christmas 2026</HeroKicker>
            <HeroTitle>The <em>Mice Trail</em></HeroTitle>
            <HeroStrap>
              The Belvoir Mice are on their way home. Follow them through five local towns, then meet them on the trail inside the Castle.
            </HeroStrap>
            <BadgeRow>
              <Pill>Five towns, then the Castle</Pill>
              <Pill>Weekly clues by email</Pill>
              <Pill>Trail included with entry</Pill>
            </BadgeRow>
            <HeroBtns>
              <BtnPrimary href="#follow-the-journey">Get the weekly clues</BtnPrimary>
              <BtnGhost as={Link} to="/christmas/mouse-map">Follow their progress</BtnGhost>
              <BtnGhost href={BOOKING_HREF}>Book Deck the Halls</BtnGhost>
            </HeroBtns>
          </HeroContent>
        </HeroWrap>

        <Section id="follow-the-journey" $bg="#fff">
          <Inner>
            <Kicker>Start here</Kicker>
            <SectionTitle>Sign up to follow <em>the journey</em></SectionTitle>
            <SectionLead>
              One town is revealed each week, with a riddle and clues to help you find a mouse. Sign up and you will hear it first — including prizes for families who find one.
            </SectionLead>
            <SignupWrap>
              <NewsletterSignUp
                tag="Christmas 2026"
                extraTags={['Mice Trail']}
                customTitle="The first clues are nearly here"
                customDescription="Your name and email. We will send the next town, the riddle, and anything you need to join the search."
                embedded
                hideSnow
              />
            </SignupWrap>
            <SignupAside>
              Already planning a visit? The Castle trail is included with every Deck the Halls ticket.{' '}
              <a href={BOOKING_HREF}>Book your entry</a>
              {' '}or{' '}
              <Link to="/christmas/mouse-map">see the journey so far</Link>.
              {' '}The search and the prize are set out in the{' '}
              <Link to="/christmas/mice-trail/terms">Mice Trail terms</Link>.
            </SignupAside>
          </Inner>
        </Section>

        <Section $bg={C.ivory}>
          <Inner>
            <Kicker>One journey</Kicker>
            <SectionTitle>From your town <em>to the Castle</em></SectionTitle>
            <SectionLead>
              There are two parts, and they belong together. Help the mice home through the towns. Then find them again when you visit Belvoir.
            </SectionLead>
            <ChapterGrid>
              <Chapter>
                <ChapterIndex>Part one · Now</ChapterIndex>
                <ChapterTitle>Five towns on the way</ChapterTitle>
                <ChapterBody>
                  The mice set off for Belvoir and wandered into five local towns. Each week we name the next one. Ten mice are hidden in every town — in streets, shops and quiet corners. Find one, give it a name, and register it to win a free family ticket to Belvoir for your family.
                </ChapterBody>
                <FactRow>
                  <Fact><strong>5</strong><span>Towns</span></Fact>
                  <Fact><strong>5</strong><span>Weeks</span></Fact>
                  <Fact><strong>10</strong><span>Mice in each town</span></Fact>
                </FactRow>
                <ChapterLink as={Link} to="/christmas/mouse-map">Follow their progress</ChapterLink>
              </Chapter>
              <Chapter $dark>
                <ChapterIndex>Part two · At Belvoir</ChapterIndex>
                <ChapterTitle>The trail inside the Castle</ChapterTitle>
                <ChapterBody $light>
                  The journey ends in the carol-themed rooms of Deck the Halls. The Mice Trail is included with your entry. Collect a trail sheet when you arrive, spot the mice as you go through the Castle, and redeem your prize at the end.
                </ChapterBody>
                <FactRow>
                  <Fact $onDark><strong>Free</strong><span>With your ticket</span></Fact>
                  <Fact $onDark><strong>On arrival</strong><span>Collect the sheet</span></Fact>
                  <Fact $onDark><strong>All ages</strong><span>A family trail</span></Fact>
                </FactRow>
                <ChapterLink $light href={BOOKING_HREF}>Book Deck the Halls</ChapterLink>
              </Chapter>
            </ChapterGrid>
          </Inner>
        </Section>

        <Section $bg="#fff">
          <Inner>
            <Kicker>The route</Kicker>
            <SectionTitle>Five weeks, then <em>Belvoir</em></SectionTitle>
            <SectionLead>
              Each town stays unnamed until its week. Sign up above and we will send it to you, with the clues for that search.
            </SectionLead>
            <Timeline aria-label="Mouse journey">
              {stops.map((stop, index) => {
                const revealed = stop.atCastle || Boolean(stop.name);
                const current = Boolean(stop.counts) || index === (nextHidden === -1 ? stops.length - 1 : nextHidden);
                return (
                  <TimelineStop key={stop.label}>
                    <StopDot $revealed={revealed || current} aria-hidden="true" />
                    <StopLabel>{stop.label}</StopLabel>
                    <StopName $hidden={!revealed}>
                      {stop.atCastle && <FontAwesomeIcon icon={faLandmark} />}
                      {revealed ? stop.name : (journey?.phase === 'hints' ? 'First clues are live' : 'To be revealed')}
                    </StopName>
                    {stop.counts && stop.counts.total > 0 && (
                      <StopName>
                        {stop.counts.found} found · {stop.counts.remaining} remaining
                      </StopName>
                    )}
                  </TimelineStop>
                );
              })}
            </Timeline>
          </Inner>
        </Section>

        <Section $bg={C.parchment}>
          <Inner>
            <Kicker>How to take part</Kicker>
            <SectionTitle>Four steps, <em>start to finish</em></SectionTitle>
            <StepGrid>
              <Step>
                <StepNum>01</StepNum>
                <StepTitle>Sign up</StepTitle>
                <StepBody>Join the list. Each week you receive the town, a riddle and the clues for that search.</StepBody>
              </Step>
              <Step>
                <StepNum>02</StepNum>
                <StepTitle>Find a mouse</StepTitle>
                <StepBody>Search the streets, shops and corners of that week’s town. Ten mice are hidden in each one.</StepBody>
              </Step>
              <Step>
                <StepNum>03</StepNum>
                <StepTitle>Name it</StepTitle>
                <StepBody>Give your mouse a name and register it. The first person to register that mouse wins a free family ticket.</StepBody>
              </Step>
              <Step>
                <StepNum>04</StepNum>
                <StepTitle>Meet them at the Castle</StepTitle>
                <StepBody>Book Deck the Halls. The trail is included, and you redeem your prize inside the Castle.</StepBody>
              </Step>
            </StepGrid>
          </Inner>
        </Section>

        <Section $bg="#fff">
          <Inner>
            <Kicker>At the Castle</Kicker>
            <SectionTitle>What happens <em>on the day</em></SectionTitle>
            <TwoCols>
              <ImgStrip>
                <img src={grottoImage} alt="A child with the Belvoir Castle Mice Trail booklet" />
              </ImgStrip>
              <div>
                <SectionLead style={{ marginBottom: '0.5rem' }}>
                  Once the mice are home, the trail is the way to explore Christmas at Belvoir. It runs through the grand rooms, each dressed as a different carol, and it does not need a separate ticket.
                </SectionLead>
                <DetailList>
                  <DetailItem>
                    <strong>Trail sheet on arrival</strong>
                    Collect it at the Castle entrance. Nothing else to book.
                  </DetailItem>
                  <DetailItem>
                    <strong>Through Deck the Halls</strong>
                    The mice are tucked into the carol-themed rooms, so the trail is also the best way to see the Castle.
                  </DetailItem>
                  <DetailItem>
                    <strong>For the whole family</strong>
                    Made with children in mind, and easy for everyone to share.
                  </DetailItem>
                  <DetailItem>
                    <strong>A prize at the end</strong>
                    Finish the sheet and redeem your prize before you leave.
                  </DetailItem>
                </DetailList>
              </div>
            </TwoCols>

            <VisitGrid>
              <VisitStep $bg={C.navy}>
                <StepNum>01</StepNum>
                <StepTitle style={{ color: '#fff' }}>Book your entry</StepTitle>
                <StepBody style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Book Deck the Halls online. Family Saver applies when you add more people to the same visit.
                </StepBody>
              </VisitStep>
              <VisitStep $bg={C.gold}>
                <StepNum style={{ color: 'rgba(255,255,255,0.75)' }}>02</StepNum>
                <StepTitle style={{ color: '#fff' }}>Collect the sheet</StepTitle>
                <StepBody style={{ color: 'rgba(255,255,255,0.88)' }}>
                  Pick up the Mice Trail sheet at the entrance. It is included with every ticket.
                </StepBody>
              </VisitStep>
              <VisitStep $bg="#1a2d5a">
                <StepNum>03</StepNum>
                <StepTitle style={{ color: '#fff' }}>Find the mice</StepTitle>
                <StepBody style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Work through the rooms, complete the trail, and redeem your prize.
                </StepBody>
              </VisitStep>
            </VisitGrid>

            <InfoGrid>
              <InfoItem>
                <InfoLabel>Dates</InfoLabel>
                <InfoValue>14 November – 30 December 2026</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Price</InfoLabel>
                <InfoValue>Included with Deck the Halls</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Where</InfoLabel>
                <InfoValue>Inside Belvoir Castle</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Trail sheet</InfoLabel>
                <InfoValue>Collected on arrival</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Booking</InfoLabel>
                <InfoValue>Castle entry only</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Address</InfoLabel>
                <InfoValue>Belvoir Castle, Grantham, NG32 1PE</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Inner>
        </Section>

        <CtaSection>
          <CtaInner>
            <CtaTitle>Book <em>Deck the Halls</em></CtaTitle>
            <CtaBody>
              The Mice Trail comes with every entry ticket. Book the Castle for the day — and sign up above if you want to follow the mice before you arrive.
            </CtaBody>
            <CtaBtnRow>
              <BtnPrimary href={BOOKING_HREF}>Book now</BtnPrimary>
              <CtaSecondary to="/christmas/your-festive-visit">Plan your visit</CtaSecondary>
            </CtaBtnRow>
            <CtaMeta>Christmas 2026 · Belvoir Castle · Trail included with Deck the Halls · <Link to="/christmas/mice-trail/terms" style={{ color: 'inherit' }}>Mice Trail terms</Link></CtaMeta>
          </CtaInner>
        </CtaSection>
      </Page>
      <Footer />
    </>
  );
};

export default MiceTrail;
